import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Badge, Tooltip, Modal } from "flowbite-react";
import { HiHome, HiUser, HiPencil, HiTrash, HiEye, HiBan } from "react-icons/hi";
import { useParams, useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { Agent, DataGridPagination, QueryParams, Group } from "@/sharedTypes";
import {
  useGetAgentByIdQuery,
  useGetAgentGroupsQuery,
} from "@/api/agentApi/agent";
import { useGetAgentSkillsQuery, useAddSubcategorySkillsMutation } from "@/api/agentApi/agentSkill";
import { useUpdateGroupMutation, useDisableGroupMutation } from "@/api/groupApi/group";
import { agentRoleLabels, agentStatusLabels } from "@/constants/agent";
import { toast } from "react-toastify";
import { getErrorMessage } from "@/utils/requestUtils";
import DataTable from "@/components/Table";
import AgentSkillsTable from "@/components/agents/AgentSkillsTable";
import SubcategorySkillDialog from "@/components/agents/SubcategorySkillDialog";
import GroupFormDialog from "@/components/groups/GroupFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import LoadingPage from "@/components/LoadingPage";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function AgentDetails() {
  const { t } = useTranslation(["agent", "common", "group"]);
  const user = useCurrentUser();
  const isAdmin = user?.agent_role === agentRoles.company_admin;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSkillDialogOpen, setIsSkillDialogOpen] = useState(false);
  
  // Group actions state
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [disableGroup, setDisableGroup] = useState<Group | null>(null);
  
  const [updateGroupMutation, { isLoading: isUpdatingGroup }] = useUpdateGroupMutation();
  const [disableGroupMutation, { isLoading: isDisablingGroup }] = useDisableGroupMutation();

  const [groupParams, setGroupParams] = useState<QueryParams>({
    filters: { is_active: true },
    pagination: { page: 1, pageSize: 15 },
  });
  const [skillParams, setSkillParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const {
    data: agent,
    isLoading: isAgentLoading,
    isError: isAgentError,
    error: agentError,
  } = useGetAgentByIdQuery(id || "", { skip: !id });

  const { data: agentGroups } = useGetAgentGroupsQuery(
    { agentId: id || "", params: groupParams },
    { skip: !id }
  );

  useEffect(() => {
    if (id) {
      setSkillParams((prev) => ({
        ...prev,
        filters: { ...prev.filters, agent_id: id },
      }));
    }
  }, [id]);

  const {
    data: agentSkills,
    isLoading: isSkillsLoading,
    isError: isSkillsError,
    error: skillsError,
  } = useGetAgentSkillsQuery(skillParams, { skip: !id });

  const [addSkills] = useAddSubcategorySkillsMutation();

  useEffect(() => {
    if (isAgentError) {
      toast.error(getErrorMessage(agentError));
    }
    if (isSkillsError) {
      toast.error(getErrorMessage(skillsError));
    }
  }, [isAgentError, agentError, isSkillsError, skillsError]);

  const groupPagination: DataGridPagination = useMemo(() => {
    const pagination = agentGroups?.pagination || {};
    return {
      pageIndex: pagination.pageIndex ?? pagination.page ?? groupParams.pagination.page,
      pageSize: pagination.pageSize ?? groupParams.pagination.pageSize,
      totalPages: pagination.totalPages ?? 1,
      totalItems: pagination.totalItems ?? pagination.total ?? 0,
    };
  }, [agentGroups, groupParams.pagination.page, groupParams.pagination.pageSize]);

  const skillPagination: DataGridPagination = useMemo(() => {
    const pagination = agentSkills?.pagination || {};
    return {
      pageIndex: pagination.pageIndex ?? pagination.page ?? skillParams.pagination.page,
      pageSize: pagination.pageSize ?? skillParams.pagination.pageSize,
      totalPages: pagination.totalPages ?? 1,
      totalItems: pagination.totalItems ?? pagination.total ?? 0,
    };
  }, [agentSkills, skillParams.pagination.page, skillParams.pagination.pageSize]);

  const handleEditGroup = (group: Group) => {
    setEditGroup(group);
  };

  const submitEditGroup = (data: Group) => {
    if (!editGroup) return;
    
    const updatedDetails = {
      group_name: data.group_name,
      group_description: data.group_description
    };

    updateGroupMutation({ 
      groupId: editGroup.group_id.toString(), 
      updatedDetails 
    })
      .unwrap()
      .then(() => {
        toast.success(t("group:updateSuccess"));
        setEditGroup(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDisableGroup = (group: Group) => {
    setDisableGroup(group);
  };

  const confirmDisableGroup = () => {
    if (!disableGroup) return;
    disableGroupMutation(disableGroup.group_id.toString())
      .unwrap()
      .then(() => {
        toast.success(t("group:disableSuccess"));
        setDisableGroup(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const groupColumns = [
    { key: "group_name", label: t("group:groupName") },
    { key: "group_description", label: t("group:description") },
    {
      key: "actions",
      label: t("common:actions"),
      render: (row: Group) => (
        <div className="flex gap-2">
           <Tooltip content={t("common:view")}>
             <Button size="xs" color="light" onClick={() => navigate(`/groups/${row.group_id}`)}>
               <HiEye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
             </Button>
           </Tooltip>
           {isAdmin && (
             <>
               <Tooltip content={t("common:edit")}>
                 <Button
                   size="xs"
                   color="light"
                   onClick={() => handleEditGroup(row)}
                 >
                   <HiPencil className="h-4 w-4 text-green-600 dark:text-green-400" />
                 </Button>
               </Tooltip>
               <Tooltip content={t("common:disable")}>
                 <Button
                   size="xs"
                   color="light"
                   onClick={() => handleDisableGroup(row)}
                 >
                   <HiBan className="h-4 w-4 text-red-600 dark:text-red-400" />
                 </Button>
               </Tooltip>
             </>
           )}
        </div>
      )
    }
  ];

  const handleGroupPageChange = (page: number) => {
    setGroupParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const handleSkillPageChange = (page: number) => {
    setSkillParams((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, page },
    }));
  };

  const handleSkillSubmit = (skills: { subcategory_id: number; skill_rate: number }[]) => {
    if (!id) return;
    addSkills({ agent_id: parseInt(id, 10), skills })
      .unwrap()
      .then(() => {
        toast.success(t("agent:addSkillsSuccess"));
        setIsSkillDialogOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const agentFields = agent as Agent | undefined;

  const getStatusBadgeColor = (status: number) => {
    switch (status) {
      case 13: // Online
        return "success";
      case 14: // Offline
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <NavbarSidebarLayout>
      {isAgentLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full space-y-6">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/agents">{t("agent:agents")}</Breadcrumb.Item>
              <Breadcrumb.Item>{agentFields?.agent_username || t("agent:details")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                  <HiUser className="h-8 w-8 text-gray-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {agentFields?.agent_username}
                  </h2>
                  <p className="text-sm text-gray-500">{agentFields?.agent_email}</p>
                </div>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("agent:displayName")}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    {agentFields?.display_name || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("agent:role")}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    {t(`agent:roles.${agentFields?.agent_role ?? 0}` as any, { defaultValue: t("agent:unknown") })}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-400">{t("agent:status")}</p>
                  <Badge color={getStatusBadgeColor(agentFields?.agent_status ?? 14)} className="w-fit">
                    {t(`agent:statuses.${agentFields?.agent_status ?? 14}` as any, { defaultValue: t("agent:unknown") })}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("agent:agentGroups")}</h3>
              <DataTable
                columns={groupColumns}
                data={(agentGroups?.items || []).filter((g: Group) => g.is_active !== false)}
                pagination={groupPagination}
                onPageChange={handleGroupPageChange}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("agent:agentSkills")}</h3>
                {isAdmin && (
                  <Button
                    color="primary"
                    onClick={() => setIsSkillDialogOpen(true)}
                  >
                    {t("agent:addSkill")}
                  </Button>
                )}
              </div>
              {isSkillsLoading ? (
                <LoadingPage />
              ) : (
                <AgentSkillsTable
                  data={agentSkills?.items || []}
                  pagination={skillPagination}
                  onPageChange={handleSkillPageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>}

      <SubcategorySkillDialog
        open={isSkillDialogOpen}
        onClose={() => setIsSkillDialogOpen(false)}
        onSubmit={handleSkillSubmit}
      />

      <GroupFormDialog
        open={!!editGroup}
        onClose={() => setEditGroup(null)}
        onSubmit={submitEditGroup}
        mode="edit"
        initialData={editGroup || undefined}
      />

      {disableGroup && (
        <ConfirmationDialog
          title={t("group:disable")}
          body={t("group:disableConfirm")}
          onConfirm={confirmDisableGroup}
          onCancel={() => setDisableGroup(null)}
          isLoading={isDisablingGroup}
        />
      )}
    </NavbarSidebarLayout>
  );
}
